import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Users, Target, Award, Building } from "lucide-react";

const TEAM_IMG = "https://images.pexels.com/photos/4956920/pexels-photo-4956920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SITE_IMG = "https://images.unsplash.com/photo-1664902578190-ec984429fabc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd29ya2Vyc3xlbnwwfHx8fDE3NzQ2MDc1NTB8MA&ixlib=rb-4.1.0&q=85";

const stats = [
  { value: "24+", label: "Ani de Experiență" },
  { value: "500+", label: "Proiecte Finalizate" },
  { value: "50+", label: "Clienți Fideli" },
  { value: "15000+", label: "m³ Beton Livrat" },
];

const values = [
  {
    icon: Target,
    title: "Calitate",
    description: "Folosim doar materiale de cea mai bună calitate, testate în laboratoare autorizate.",
  },
  {
    icon: Users,
    title: "Profesionalism",
    description: "Echipa noastră este formată din specialiști cu experiență în industria construcțiilor.",
  },
  {
    icon: Award,
    title: "Fiabilitate",
    description: "Încercăm să respectăm întotdeauna termenele de livrare și specificațiile tehnice solicitate.",
  },
  {
    icon: Building,
    title: "Inovație",
    description: "Investim continuu în echipamente moderne și în formarea profesională a angajaților.",
  },
];

const certifications = [
  { name: "ISO 9001:2015", description: "Sistem de Management al Calității" },
  { name: "ISO 14001:2015", description: "Sistem de Management de Mediu" },
  { name: "SR EN 206-1", description: "Beton - Specificații, performanțe" },
  { name: "Agrementare BRML", description: "Certificare Biroul Român de Metrologie Legală" },
];

export const About = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-b border-[#D4D4D4]" data-testid="about-hero">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Despre Noi
            </p>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter uppercase mb-6">
              CONSTRUIM<br />ÎNCREDERE
            </h1>
            <p className="text-lg text-[#737373] max-w-2xl font-medium leading-relaxed">
              SC SOLAR CONSTRUCT SRL este un furnizor de beton readymix cu o tradiție de peste 10 ani în industria construcțiilor din România.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="stats-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-black text-[#FACC15] mb-2">{stat.value}</p>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/60">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="story-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <img 
                src={TEAM_IMG} 
                alt="Echipa Solar Construct" 
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
                Povestea Noastră
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                DE LA VIZIUNE LA REALITATE
              </h2>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-6">
                Am început activitatea în 2002 cu o singură stație de betoane și o autobetonieră. Astăzi, suntem unul dintre cei mai importanți furnizori de beton din județul Vrancea.
              </p>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-6">
                Creșterea noastră constantă se datorează angajamentului față de calitate și satisfacția clienților. Fiecare proiect, indiferent de dimensiune, primește aceeași atenție la detalii.
              </p>
              <p className="text-base font-medium leading-relaxed text-[#737373]">
                Ne mândrim cu relațiile pe termen lung construite cu clienții noștri și cu faptul că am contribuit la sute de proiecte de construcții din regiune.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-b border-[#D4D4D4]" data-testid="values-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
              Valorile Noastre
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              PRINCIPIILE CARE NE GHIDEAZĂ
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="p-8 bg-white border border-[#D4D4D4] hover:border-[#FACC15] transition-colors"
                data-testid={`value-${index}`}
              >
                <value.icon className="w-10 h-10 text-[#FACC15] mb-4" />
                <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-[#737373] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="certifications-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
                Certificări
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                CALITATE CERTIFICATĂ
              </h2>
              <p className="text-base font-medium leading-relaxed text-[#737373] mb-8">
                Produsele noastre sunt certificate conform standardelor europene și naționale.
              </p>
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 border-l-2 border-[#FACC15] bg-[#FAFAFA]"
                    data-testid={`cert-${index}`}
                  >
                    <div>
                      <p className="font-bold">{cert.name}</p>
                      <p className="text-sm text-[#737373]">{cert.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <img 
                src={SITE_IMG} 
                alt="Șantier de construcții" 
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="about-cta-section">
        <div className="px-6 md:px-12 lg:px-24 text-center">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FACC15] mb-4">
            Hai să Construim Împreună
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8 text-white">
            PREGĂTIT PENTRU PROIECTUL TĂU?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-[#FACC15] text-[#0A0A0A] hover:bg-yellow-400 rounded-none font-bold tracking-wider uppercase text-sm px-8 py-6"
              data-testid="about-services-btn"
            >
              <Link to="/servicii">
                Vezi Serviciile
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] rounded-none font-bold tracking-wider uppercase text-sm px-8 py-6"
              data-testid="about-contact-btn"
            >
              <Link to="/contact">Contactează-ne</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
